import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import BillingSummary from './BillingSummary';
import BillingEntryDetails from './BillingEntryDetails';
import BillingEstimate from './BillingEstimate';
import { money } from './clientPortalSelectors';
import { displayDate } from '../common/datePresentation';
import { BILLING_TYPES, billingYears, billingYearLabel, billingTypeLabel, billingEntryDescription, billingHistory, billingTotals } from './clientBillingPresentation';

export default function ClientBilling({ ledger, client, invoices, estimate, billingYear, onBillingYear }) {
  const { t: tr, i18n } = useTranslation();
  const [requestedYear, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [type, setType] = useState('');
  const [limit, setLimit] = useState(5);
  const [showPrevious, setShowPrevious] = useState(Boolean(billingYear && billingYear.schoolYearStartYear !== billingYear.currentSchoolYearStartYear));
  const [yearCount, setYearCount] = useState(6);
  const years = billingYears(ledger);
  const localYear = years.some(item => String(item.schoolYearStartYear) === requestedYear) ? requestedYear : String(years[0] ? years[0].schoolYearStartYear : '');
  const year = billingYear ? String(billingYear.schoolYearStartYear) : localYear;
  const yearOptions = billingYear ? Array.from({
    length: Math.min(billingYear.currentSchoolYearStartYear - 999, Math.max(yearCount, billingYear.currentSchoolYearStartYear - Number(year) + 1)),
  }, (_, index) => billingYear.currentSchoolYearStartYear - index) : [];
  useEffect(() => {
    setMonth('');
    setType('');
    setLimit(5);
  }, [year]);
  const ready = ledger && ledger.summary && ledger.summary.assignmentStatus === 'ready';
  const entries = ledger && ledger.entries ? ledger.entries : [];
  const yearEntries = billingHistory(entries, year);
  const months = [...new Set(yearEntries.map(entry => String(entry.date).slice(0, 7)).filter(value => /^\d{4}-\d{2}$/.test(value)))].sort().reverse();
  const filtered = billingHistory(entries, year, month, type);
  const totals = billingTotals(filtered);
  const visibleInvoices = invoices.filter(invoice => (!billingYear || invoice.schoolYearStartYear === billingYear.schoolYearStartYear) &&
    (invoice.invoice || /^https?:\/\//i.test(invoice.invoiceUrl || '')));
  const chooseYear = value => { setYear(value); setMonth(''); setType(''); setLimit(5); };
  return (
    <div className="portal-billing">
      {billingYear && (
        <section className="client-portal__card">
          <h2>{tr('client:billing.year')} {billingYearLabel(year)}</h2>
          <label htmlFor="portal-previous-years"><input
            id="portal-previous-years"
            type="checkbox"
            checked={showPrevious}
            onChange={event => {
              setShowPrevious(event.target.checked);
              if (!event.target.checked && onBillingYear) onBillingYear(billingYear.currentSchoolYearStartYear);
            }}
          /> {tr('client:billing.previousYears')}
          </label>
          {showPrevious && (
          <label className="d-block" htmlFor="portal-requested-billing-year">{tr('client:billing.selectYear')}
            <select id="portal-requested-billing-year" className="form-control" value={year} onChange={event => onBillingYear(Number(event.target.value))}>
              {yearOptions.map(value => <option key={value} value={value}>{billingYearLabel(value)}</option>)}
            </select>
          </label>
          )}
          {showPrevious && <button type="button" className="btn manage-teal-button" onClick={() => setYearCount(yearCount + 6)}>{tr('client:billing.olderYears')}</button>}
        </section>
      )}
      <BillingSummary ledger={ledger} client={client} selectedYear={year} onYear={billingYear ? null : chooseYear} />
      <BillingEstimate estimate={estimate} />
      {ready && year && (
      <section className="client-portal__card">
        <h2>{tr('client:billing.history')} · {billingYearLabel(year)}</h2>
        <p className="text-muted">{tr('client:billing.filterScope')}</p>
        <div className="portal-billing__filters">
          <label htmlFor="portal-billing-period">{tr('client:billing.period')}
            <select id="portal-billing-period" className="form-control" value={month} onChange={event => { setMonth(event.target.value); setLimit(5); }}>
              <option value="">{tr('client:billing.wholeYear')}</option>
              {months.map(value => <option key={value} value={value}>{value.split('-').reverse().join('.')}</option>)}
            </select>
          </label>
          <label htmlFor="portal-billing-type">{tr('client:billing.type')}
            <select id="portal-billing-type" className="form-control" value={type} onChange={event => { setType(event.target.value); setLimit(5); }}>
              <option value="">{tr('client:billing.allTypes')}</option>
              {BILLING_TYPES.map(value => <option key={value} value={value}>{billingTypeLabel(value, tr)}</option>)}
            </select>
          </label>
        </div>
        <div className="portal-billing__totals">
          <span>{tr('client:billing.paymentsTotal')}: <strong>{money(totals.payments / 100, i18n.language)}</strong></span>
          <span>{tr('client:billing.chargesTotal')}: <strong>{money(totals.charges / 100, i18n.language)}</strong></span>
        </div>
        {filtered.length ? (
          <table className="portal-billing__table">
            <thead><tr><th scope="col">{tr('client:billing.date')}</th><th scope="col">{tr('client:billing.operation')}</th><th scope="col">{tr('client:billing.amount')}</th></tr></thead>
            <tbody>{filtered.slice(0, limit).map(entry => (
              <tr key={entry.id}>
                <td>{displayDate(entry.date, i18n.language)}</td>
                <td><strong>{billingTypeLabel(entry.type, tr)}</strong><div className="text-muted">{billingEntryDescription(entry, tr)}</div><BillingEntryDetails entry={entry} client={client} /></td>
                <td className={entry.amount > 0 ? 'portal-billing__credit' : ''}>{entry.amount > 0 ? '+' : ''}{money(entry.amount, i18n.language)}</td>
              </tr>
            ))}
            </tbody>
          </table>
        ) : (
          <div className="portal-billing__empty"><p>{tr('client:billing.empty')}</p>
            {(month || type) && <button type="button" className="btn manage-teal-button" onClick={() => { setMonth(''); setType(''); setLimit(5); }}>{tr('client:billing.clear')}</button>}
          </div>
        )}
        <div className="portal-billing__footer">
          <span role="status">{tr('client:billing.visible', { shown: Math.min(limit, filtered.length), total: filtered.length })}</span>
          {limit < filtered.length && <button type="button" className="btn manage-teal-button" onClick={() => setLimit(limit + 10)}>{tr('client:billing.more')}</button>}
        </div>
      </section>
      )}
      {visibleInvoices.length > 0 && (
      <section className="client-portal__card"><h2>{tr('client:invoices_25')}</h2>
        {visibleInvoices.map(invoice => (
          <article className="client-portal__detail-row" key={invoice.id}>
            {invoice.invoice && <strong>{invoice.invoice}</strong>}
            {invoice.date && <div>{displayDate(invoice.date, i18n.language)}</div>}
            {Number.isFinite(invoice.amount) && <div>{money(Math.abs(invoice.amount), i18n.language)}</div>}
            {/^https?:\/\//i.test(invoice.invoiceUrl || '') && <a href={invoice.invoiceUrl} target="_blank" rel="noopener noreferrer">{tr('client:openInvoice_27')}</a>}
          </article>
        ))}
      </section>
      )}
    </div>
  );
}
ClientBilling.propTypes = {
  ledger: PropTypes.object,
  client: PropTypes.object.isRequired,
  invoices: PropTypes.array,
  estimate: PropTypes.object,
  billingYear: PropTypes.object,
  onBillingYear: PropTypes.func,
};
ClientBilling.defaultProps = { ledger: null, invoices: [], estimate: null, billingYear: null, onBillingYear: null };
